import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import * as LucideIcons from "lucide-react";

interface Topic {
  id: number;
  name: string;
  description: string | null;
  icon_name: string | null;
  color_class: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TopicEditorProps {
  topic?: Topic;
  onSave: () => void;
  onCancel?: () => void;
}

const iconOptions = [
  'MessageCircle', 'BookOpen', 'Shield', 'Heart', 'History', 'Wrench', 
  'Users', 'Settings', 'Info', 'Star', 'Lock', 'Eye', 'Tool'
];

const colorOptions = [
  { name: 'Toxic Green', value: 'bg-carbon/80 border-toxic-green/30 hover:border-toxic-green' },
  { name: 'Electric Violet', value: 'bg-carbon/80 border-electric-violet/30 hover:border-electric-violet' },
  { name: 'Neon Red', value: 'bg-carbon/80 border-neon-red/30 hover:border-neon-red' },
  { name: 'Ice White', value: 'bg-carbon/80 border-ice-white/30 hover:border-ice-white' }
];

const TopicEditor = ({ topic, onSave, onCancel }: TopicEditorProps) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon_name: 'BookOpen',
    color_class: 'bg-carbon/80 border-toxic-green/30 hover:border-toxic-green',
    display_order: 0,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (topic) {
      setFormData({
        name: topic.name,
        description: topic.description || '',
        icon_name: topic.icon_name || 'BookOpen',
        color_class: topic.color_class || 'bg-carbon/80 border-toxic-green/30 hover:border-toxic-green',
        display_order: topic.display_order,
        is_active: topic.is_active,
      });
    }
  }, [topic]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Topic name is required",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const topicData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        icon_name: formData.icon_name,
        color_class: formData.color_class,
        display_order: formData.display_order,
        is_active: formData.is_active,
        updated_at: new Date().toISOString(),
      };

      if (topic) {
        // Update existing topic
        const { error } = await supabase
          .from('kinkipedia_topics')
          .update(topicData)
          .eq('id', topic.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Topic updated successfully",
        });
      } else {
        // Create new topic
        const { error } = await supabase
          .from('kinkipedia_topics')
          .insert([topicData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Topic created successfully",
        });
      }

      onSave();
    } catch (error) {
      console.error('Error saving topic:', error);
      toast({
        title: "Error",
        description: "Failed to save topic",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get the icon component
  const IconComponent = (LucideIcons as any)[formData.icon_name] || LucideIcons.BookOpen;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className="text-ice-white">Topic Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Topic name"
            className="bg-carbon border-ice-white/20 text-ice-white"
          />
        </div>

        <div>
          <Label htmlFor="display_order" className="text-ice-white">Display Order</Label>
          <Input
            id="display_order"
            type="number"
            value={formData.display_order}
            onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            placeholder="0"
            className="bg-carbon border-ice-white/20 text-ice-white"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="description" className="text-ice-white">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Topic description"
          className="bg-carbon border-ice-white/20 text-ice-white"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="icon" className="text-ice-white">Icon</Label>
          <Select
            value={formData.icon_name}
            onValueChange={(value) => setFormData({ ...formData, icon_name: value })}
          >
            <SelectTrigger className="bg-carbon border-ice-white/20 text-ice-white">
              <SelectValue placeholder="Select icon" />
            </SelectTrigger>
            <SelectContent className="bg-carbon border-ice-white/20">
              {iconOptions.map((icon) => {
                const Icon = (LucideIcons as any)[icon];
                return (
                  <SelectItem key={icon} value={icon}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {icon}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="color" className="text-ice-white">Color Theme</Label>
          <Select
            value={formData.color_class}
            onValueChange={(value) => setFormData({ ...formData, color_class: value })}
          >
            <SelectTrigger className="bg-carbon border-ice-white/20 text-ice-white">
              <SelectValue placeholder="Select color" />
            </SelectTrigger>
            <SelectContent className="bg-carbon border-ice-white/20">
              {colorOptions.map((color) => (
                <SelectItem key={color.value} value={color.value}>
                  {color.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
        />
        <Label htmlFor="is_active" className="text-ice-white">Active</Label>
      </div>

      {/* Preview */}
      <Card className={`${formData.color_class} border text-ice-white`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <IconComponent className="w-8 h-8 text-toxic-green" />
          </div>
          <CardTitle className="text-lg leading-tight text-ice-white">{formData.name || "Topic Name"}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-ice-white/70 text-sm leading-relaxed">
            {formData.description || "Topic description"}
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-2 pt-4 border-t border-ice-white/20">
        {onCancel && (
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-ice-white/30 text-ice-white hover:bg-carbon/50"
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-neon-red hover:bg-neon-red/80 text-white"
        >
          {loading ? "Saving..." : (topic ? "Update Topic" : "Create Topic")}
        </Button>
      </div>
    </div>
  );
};

export default TopicEditor;